import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { DBDef_Action, DBDef_OrderGroup } from '@app/pathway-shared';
import { filterDuplicates, ImplementationMissingException, MUSTNeverHappenException, voidFunction } from '@nu-art/ts-common';
import { Renderer_Transition } from '../../../renderers/Renderer_Transition.js';
import { Header_Pathway } from '../../../headers/Header_Pathway.js';
import { Header_PathwayTransition } from '../../../headers/Header_PathwayTransition.js';
import { Dialog_Confirmation } from '@app/styles/frontend/ui-manager/dialogs/ConfirmationDialog/index';
import { DataGrid } from '@app/styles';
import { ModuleFE_Action, ModuleFE_OrderGroup, ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../../../../_entity.js';
import { basicSyncEventRefresh, canEditPathway } from '../../../utils.js';
import { Section_AllEntityVariables } from '../common-components/Section_AllEntityVariables.js';
import { Component_CollapsablePrettyPrint } from '../common-components/Component_CollapsablePrettyPrint.js';
import { ModuleFE_PathwayEditor } from '../../../../../modules/index.js';
import { Renderer_PathwayItemChildSelector } from '../../../renderers/Renderer_PathwayItemChildSelector.js';
import { Renderer_OrderExpressionIndicator } from '../../../renderers/Renderer_OrderExpressionIndicator.js';
export class Renderer_PathwayTransitionDetails extends ComponentSync {
    // ######################## Lifecycle ########################
    __onPathwayTransitionUpdated(...params) {
        const pathwayTransitionId = this.state.pathwayTransition.get('_id');
        if (!pathwayTransitionId)
            return;
        basicSyncEventRefresh(pathwayTransitionId, params, () => this.props.functions.selectPathwayTransition(pathwayTransitionId), () => this.props.functions.deselectPathwayTransition());
    }
    deriveStateFromProps(nextProps, state) {
        state.pathwayTransition = nextProps.pathwayTransition;
        state.targetState = ModuleFE_PathwayState.cache.unique(state.pathwayTransition.item.targetId);
        state.pathwaysToRemove ??= [];
        return state;
    }
    // ######################## Logic ########################
    deleteTransition = async () => {
        const pathwayTransition = this.state.pathwayTransition;
        const sourceId = pathwayTransition.item.sourceId;
        const viewedPathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!viewedPathwayId)
            throw new MUSTNeverHappenException('cannot view this page without focused pathway id');
        this.state.pathwaysToRemove.push(viewedPathwayId);
        Dialog_Confirmation.show({
            header: 'Are you sure?',
            buttons: [
                {
                    label: 'Yes, Delete',
                    isAsync: true,
                    action: async () => {
                        await ModuleFE_PathwayTransition._v1.deletePathwayTransition({
                            transitionsToRemove: [pathwayTransition.item],
                            pathwayIds: this.state.pathwaysToRemove
                        }).executeSync();
                        this.props.functions.selectPathwayState(sourceId);
                    }
                },
                {
                    label: 'Don\'t Delete',
                    isPreferred: true,
                    action: voidFunction,
                }
            ],
            content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to delete transition" }), _jsx(Renderer_Transition, { transition: pathwayTransition.item, className: 'green' }), _jsx(Renderer_PathwayItemChildSelector, { pathwayId: this.state.pathwaysToRemove[0], childFilter: child => this.state.pathwayTransition.get('pathwayIds').includes(child._id), onChangeCallback: pathwayIds => this.setState({ pathwaysToRemove: filterDuplicates([viewedPathwayId, ...pathwayIds]) }) })] })
        });
    };
    copyTransition = async () => {
        const transition = this.state.pathwayTransition.item;
        if (!transition)
            return;
        const dbTransition = ModuleFE_PathwayTransition.cache.unique(transition._id);
        if (!dbTransition)
            return;
        await ModuleFE_PathwayTransition.copyTransitionToClipboard(dbTransition);
    };
    copyTransitionOrders = async () => {
        const transition = this.state.pathwayTransition.item;
        if (!transition)
            return;
        const dbTransition = ModuleFE_PathwayTransition.cache.unique(transition._id);
        if (!dbTransition)
            return;
        await ModuleFE_PathwayTransition.copyTransitionOrdersToClipboard(dbTransition);
    };
    pasteTransitionOrders = async () => {
        const pathwayTransition = (await ModuleFE_PathwayTransition.getCopiedTransitionOrders());
        if (!pathwayTransition)
            return;
        const targetTransitionId = this.state.pathwayTransition.item._id;
        if (!targetTransitionId)
            return;
        ModuleFE_PathwayTransition._v1.copyTransitionOrders({ pathwayTransition: pathwayTransition.data, targetTransitionId }).executeSync();
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'match_parent pathway-transition-details', style: { padding: '30px', gap: '10px' }, children: [this.renderHeader(), this.renderBody()] });
    }
    // ######################## Render - Header ########################
    renderHeader = () => {
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        const pathway = ModuleFE_Pathway.cache.unique(pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`Could not get pathway for id ${pathwayId}`);
        const canEdit = canEditPathway(pathway);
        return _jsxs(_Fragment, { children: [this.renderTopButtons(), _jsx(Header_Pathway, { pathway: new EditableDBItemV3(pathway, ModuleFE_Pathway), allowActions: canEdit, functions: this.props.functions, clickableTitle: true }), _jsx(Header_PathwayTransition, { pathwayTransition: this.state.pathwayTransition, functions: this.props.functions, clickableTitle: true, actions: canEdit ? [
                        { label: 'Edit Transition', action: this.props.toggleEdit },
                        { label: 'Copy Transition', action: this.copyTransition },
                        { label: 'Delete Transition', action: this.deleteTransition },
                        { label: 'Copy Transition Orders', action: this.copyTransitionOrders },
                        { label: 'Paste Transition Orders', action: this.pasteTransitionOrders },
                        {
                            label: 'Edit Resolution Function',
                            action: () => this.props.functions.selectResolutionFunctionEditor(this.state.pathwayTransition.item._id),
                        }
                    ] : undefined })] });
    };
    renderTopButtons = () => {
        return _jsx(LL_H_C, { className: 'pathway__top-buttons', children: _jsx(Button, { variant: 'text', className: 'pathway__top-buttons__back', onClick: this.props.functions.deselectPathway, children: '< Back to list' }) });
    };
    // ######################## Render - Body ########################
    renderBody = () => {
        return _jsxs(LL_V_L, { className: 'details-renderer__wrapper', children: [_jsxs(DataGrid.Grid, { children: [this.renderTargetPrecondition(), this.renderAlert(), this.renderResolutionFunction(), this.renderTests(), this.renderTreatments(), this.renderNurse(), this.renderConsult(), this.renderMetapathwayActions()] }), this.renderHcsVariables()] });
    };
    renderTargetPrecondition = () => {
        const targetState = this.state.targetState;
        if (!targetState)
            return;
        const preconditions = targetState.preconditions?.map(precondition => {
            switch (precondition.itemPointer.dbKey) {
                case DBDef_Action.dbKey: {
                    const order = ModuleFE_Action.cache.unique(precondition.itemPointer.id);
                    return { id: precondition.itemPointer.id, label: order?.name ?? 'N/A', dbKey: DBDef_Action.dbKey };
                }
                case DBDef_OrderGroup.dbKey: {
                    const group = ModuleFE_OrderGroup.cache.unique(precondition.itemPointer.id);
                    return { id: precondition.itemPointer.id, label: group?.name ?? 'N/A', dbKey: DBDef_OrderGroup.dbKey };
                }
                default:
                    throw new ImplementationMissingException(`Missing implementation for key ${precondition.itemPointer.dbKey}`);
            }
        });
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Target State Preconditions" }), _jsx(LL_V_L, { children: preconditions?.map(precondition => _jsx(DataGrid.Data, { children: precondition.dbKey === DBDef_Action.dbKey
                            ? _jsxs(LL_H_C, { className: 'order-expression-row', children: [precondition.label, _jsx(Renderer_OrderExpressionIndicator, { orderId: precondition.id, editMode: false })] })
                            : precondition.label }, precondition.id)) })] });
    };
    renderAlert = () => {
        const transitionAlert = this.state.pathwayTransition.get('alert');
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Alert" }), _jsx(DataGrid.Data, { children: transitionAlert ?? '-' })] });
    };
    renderResolutionFunction = () => {
        const transition = this.state.pathwayTransition.item;
        return _jsxs(_Fragment, { children: [_jsx(Component_CollapsablePrettyPrint, { removeIndentation: true, functions: this.props.functions, transitionId: transition._id }), _jsx(DataGrid.Data, {})] });
    };
    renderTests = () => {
        const tests = this.state.pathwayTransition.item.testsIds;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Tests" }), !tests?.length && _jsx(DataGrid.Data, { children: "-" }), !!tests?.length && _jsx(LL_V_L, { children: tests.map(this.renderActionPointer) })] });
    };
    renderTreatments = () => {
        const treatments = this.state.pathwayTransition.item.treatmentsIds;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Treatments" }), !treatments?.length && _jsx(DataGrid.Data, { children: "-" }), !!treatments?.length && _jsx(LL_V_L, { children: treatments.map(this.renderActionPointer) })] });
    };
    renderConsult = () => {
        const consults = this.state.pathwayTransition.item.consultIds;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Consult" }), !consults?.length && _jsx(DataGrid.Data, { children: "-" }), !!consults?.length && _jsx(LL_V_L, { children: consults.map(this.renderActionPointer) })] });
    };
    renderNurse = () => {
        const nurses = this.state.pathwayTransition.item.nurseIds;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Nurse" }), !nurses?.length && _jsx(DataGrid.Data, { children: "-" }), !!nurses?.length && _jsx(LL_V_L, { children: nurses.map(this.renderActionPointer) })] });
    };
    renderActionPointer = (pointer) => {
        switch (pointer.dbKey) {
            case DBDef_Action.dbKey: {
                const order = ModuleFE_Action.cache.unique(pointer.id);
                if (!order)
                    throw new MUSTNeverHappenException(`Could not find order for id ${pointer.id}`);
                return _jsx(DataGrid.Data, { children: _jsxs(LL_H_C, { className: 'order-expression-row', children: [order.name, _jsx(Renderer_OrderExpressionIndicator, { orderId: order._id, editMode: false, expressionId: pointer.expressionId })] }) }, order._id);
            }
            case DBDef_OrderGroup.dbKey: {
                const group = ModuleFE_OrderGroup.cache.unique(pointer.id);
                if (!group)
                    throw new MUSTNeverHappenException(`Could not find order group for id ${pointer.id}`);
                return _jsx(DataGrid.Data, { children: group.name }, group._id);
            }
        }
    };
    renderMetapathwayActions = () => {
        const transition = this.state.pathwayTransition.item;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Metapathway Actions " }), !transition.metaPathwayActions?.length && _jsx(DataGrid.Data, { children: "-" }), !!transition.metaPathwayActions?.length && _jsx(LL_V_L, { children: transition.metaPathwayActions.map((action, index) => {
                        return _jsxs(React.Fragment, { children: [_jsx(DataGrid.Data, { children: action.type }), action.protocolIds?.map(protocolId => {
                                    const protocol = ModuleFE_Pathway.cache.unique(protocolId);
                                    if (!protocol)
                                        throw new MUSTNeverHappenException(`Could not find pathway protocol for id ${protocolId}`);
                                    return _jsxs(DataGrid.Data, { className: 'indent', children: ["- ", protocol.label] }, protocolId);
                                })] }, index);
                    }) })] });
    };
    renderHcsVariables = () => {
        return _jsx(Section_AllEntityVariables, { functions: this.props.functions, pathwayTransitionId: this.state.pathwayTransition.get('_id') });
    };
}
//# sourceMappingURL=Renderer_PathwayTransitionDetails.js.map